import React, { useState } from 'react';
import { motion } from 'motion/react';
import { Zap, ToggleLeft, ToggleRight, Info } from 'lucide-react';

const EnergySettings: React.FC = () => {
  const [settings, setSettings] = useState({
    autoOptimization: true,
    predictiveAnalysis: true,
    realTimeMonitoring: true,
    alertThreshold: 85,
    dataRetention: 12,
    exportFormat: 'csv',
    updateInterval: 5,
  });

  const toggleSetting = (key: keyof typeof settings) => {
    if (typeof settings[key] === 'boolean') {
      setSettings((prev) => ({ ...prev, [key]: !prev[key] }));
    }
  };

  const updateSetting = (key: keyof typeof settings, value: any) => {
    setSettings((prev) => ({ ...prev, [key]: value }));
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center gap-4">
        <div className="rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-600 p-3">
          <Zap className="h-6 w-6 text-white" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Energy Settings</h1>
          <p className="text-gray-600">Configure energy monitoring and optimization parameters</p>
        </div>
      </div>

      {/* Monitoring Settings */}
      <div className="space-y-6">
        <h3 className="text-lg font-semibold text-gray-900">Monitoring & Analysis</h3>

        <div className="space-y-4">
          <div className="flex items-center justify-between rounded-2xl border border-gray-100 bg-gray-50/50 p-4">
            <div className="flex items-center gap-3">
              <div className="rounded-xl bg-blue-100 p-2">
                <Zap className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <h4 className="font-semibold text-gray-900">Auto-Optimization</h4>
                <p className="text-sm text-gray-600">Automatically optimize energy consumption</p>
              </div>
            </div>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => toggleSetting('autoOptimization')}
              className={`flex items-center rounded-full p-1 transition-all duration-200 ${
                settings.autoOptimization ? 'bg-emerald-600' : 'bg-gray-300'
              }`}
            >
              {settings.autoOptimization ? (
                <ToggleRight className="h-6 w-6 text-white" />
              ) : (
                <ToggleLeft className="h-6 w-6 text-gray-600" />
              )}
            </motion.button>
          </div>

          <div className="flex items-center justify-between rounded-2xl border border-gray-100 bg-gray-50/50 p-4">
            <div className="flex items-center gap-3">
              <div className="rounded-xl bg-purple-100 p-2">
                <Info className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <h4 className="font-semibold text-gray-900">Predictive Analysis</h4>
                <p className="text-sm text-gray-600">AI-powered energy consumption predictions</p>
              </div>
            </div>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => toggleSetting('predictiveAnalysis')}
              className={`flex items-center rounded-full p-1 transition-all duration-200 ${
                settings.predictiveAnalysis ? 'bg-emerald-600' : 'bg-gray-300'
              }`}
            >
              {settings.predictiveAnalysis ? (
                <ToggleRight className="h-6 w-6 text-white" />
              ) : (
                <ToggleLeft className="h-6 w-6 text-gray-600" />
              )}
            </motion.button>
          </div>

          <div className="flex items-center justify-between rounded-2xl border border-gray-100 bg-gray-50/50 p-4">
            <div className="flex items-center gap-3">
              <div className="rounded-xl bg-green-100 p-2">
                <Zap className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <h4 className="font-semibold text-gray-900">Real-time Monitoring</h4>
                <p className="text-sm text-gray-600">Live energy consumption tracking</p>
              </div>
            </div>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => toggleSetting('realTimeMonitoring')}
              className={`flex items-center rounded-full p-1 transition-all duration-200 ${
                settings.realTimeMonitoring ? 'bg-emerald-600' : 'bg-gray-300'
              }`}
            >
              {settings.realTimeMonitoring ? (
                <ToggleRight className="h-6 w-6 text-white" />
              ) : (
                <ToggleLeft className="h-6 w-6 text-gray-600" />
              )}
            </motion.button>
          </div>
        </div>
      </div>

      {/* Thresholds & Alerts */}
      <div className="space-y-6">
        <h3 className="text-lg font-semibold text-gray-900">Thresholds & Alerts</h3>

        <div className="grid grid-cols-2 gap-6">
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">Alert Threshold (%)</label>
            <div className="rounded-xl border border-gray-200 bg-white p-3">
              <input
                type="range"
                min="50"
                max="100"
                value={settings.alertThreshold}
                onChange={(e) => updateSetting('alertThreshold', parseInt(e.target.value))}
                className="w-full accent-blue-600"
              />
              <div className="mt-2 flex justify-between text-sm text-gray-600">
                <span>50%</span>
                <span className="font-semibold text-blue-600">{settings.alertThreshold}%</span>
                <span>100%</span>
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">Update Interval (seconds)</label>
            <select
              value={settings.updateInterval}
              onChange={(e) => updateSetting('updateInterval', parseInt(e.target.value))}
              className="w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-gray-900 focus:border-blue-300 focus:ring-2 focus:ring-blue-100 focus:outline-none"
            >
              <option value={1}>1 second</option>
              <option value={5}>5 seconds</option>
              <option value={10}>10 seconds</option>
              <option value={30}>30 seconds</option>
              <option value={60}>1 minute</option>
            </select>
          </div>
        </div>
      </div>

      {/* Data Management */}
      <div className="space-y-6">
        <h3 className="text-lg font-semibold text-gray-900">Data Management</h3>

        <div className="grid grid-cols-2 gap-6">
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">Data Retention (months)</label>
            <select
              value={settings.dataRetention}
              onChange={(e) => updateSetting('dataRetention', parseInt(e.target.value))}
              className="w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-gray-900 focus:border-blue-300 focus:ring-2 focus:ring-blue-100 focus:outline-none"
            >
              <option value={3}>3 months</option>
              <option value={6}>6 months</option>
              <option value={12}>12 months</option>
              <option value={24}>24 months</option>
              <option value={36}>36 months</option>
            </select>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">Export Format</label>
            <select
              value={settings.exportFormat}
              onChange={(e) => updateSetting('exportFormat', e.target.value)}
              className="w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-gray-900 focus:border-blue-300 focus:ring-2 focus:ring-blue-100 focus:outline-none"
            >
              <option value="csv">CSV</option>
              <option value="json">JSON</option>
              <option value="xlsx">Excel</option>
              <option value="pdf">PDF Report</option>
            </select>
          </div>
        </div>
      </div>

      {/* Save Button */}
      <div className="flex justify-end gap-3 pt-6">
        <button className="rounded-xl bg-gray-200 px-6 py-3 font-medium text-gray-700 transition-all hover:bg-gray-300">
          Reset to Defaults
        </button>
        <button className="rounded-xl bg-emerald-600 px-6 py-3 font-medium text-white transition-all hover:bg-emerald-700">
          Save Settings
        </button>
      </div>
    </div>
  );
};

export default EnergySettings;
